import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Store} from 'redux';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {TaskSourcesEnum} from '../../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useTimeout} from '../../../../srcCommon/helpers/hookHelpers';
import {findMentionedTeammates} from '../../../helpers/models/commentHelpers';
import {CommentStatusesEnum} from '../../../models/api/commentModel';
import {MessageStatusesEnum} from '../../../models/api/messageModel';
import {OnboardingTipTypesEnum, StartGuideTaskTypesEnum} from '../../../models/api/preferencesModel';
import {AppState} from '../../../store/appState';
import {getComments} from '../../../store/data/dataSelectors';
import {getTeammates} from '../../../store/data/teammates/teammatesSelectors';
import {InfoBubblesContext, InfoBubblesContextProps, InfoBubblesMap} from './infoBubblesContext';

/*
 * Constants.
 */

const demoDraftBlurbSample = 'Hi Jamie, Your time machine will arrive tomorrow';

/*
 * Props.
 */

interface InfoBubblesProviderProps {
  /** The store used to listen for changes that should trigger an info bubble. */
  store: Store<AppState>;
  /** The info bubbles that have already been seen by the teammate. */
  seenOnboardingTips: ReadonlyArray<OnboardingTipTypesEnum>;
  /** Handler to update teammate preference. */
  updateSeenOnboardingTips(bubbles: ReadonlyArray<OnboardingTipTypesEnum>, shouldMarkUnseen?: boolean): void;
  /** The id of the user. */
  userId: number;
  /** Whether the guided demo is currently in progress. */
  isGuidedDemoActive: boolean;
  /** Whether the user is currently in assigned to me. */
  isInAssignedToMe: boolean;
  /** Handler to navigate to the demo conversation. */
  navigateToDemoConversation: Handler;
  /** Whether the teammate tour is currently in progress. */
  isTeammateTourActive: boolean;
  /** An optional active sequence of bubbles. */
  activeSequence?: ReadonlyArray<OnboardingTipTypesEnum>;
  /** The id of the guided demo conversation. */
  guidedDemoConversationId?: number;
  /** The startguide tasks that have already been completed by the teammate. */
  completedStartGuideTasks: ReadonlyArray<StartGuideTaskTypesEnum>;
  /** Whether the user is not a global admin and has the non global admin startguide feature flag. */
  hasNonGlobalAdminStartGuideFlag: boolean;
  /** Whether the user is in a team context with unread conversations. */
  shouldShowReadStatusBubble: boolean;
  /** Whether the user is in a private inbox and should see a private comment bubble. */
  shouldShowPrivateCommentBubble: boolean;
  /** Whether we should watch for navigation away from the demo inbox. */
  shouldTrackNavigationFromDemoInbox: boolean;
  /** Whether the user has commented on a conversation and we should show an archive infoBubble. */
  shouldShowFirstArchiveInfoBubble: boolean;
  /** Handler to open the start guide. */
  openStartGuide: Handler;
}

/*
 * Component.
 */

export const InfoBubblesProvider: FC<InfoBubblesProviderProps> = (props) => {
  const {
    store,
    seenOnboardingTips,
    updateSeenOnboardingTips,
    activeSequence,
    userId,
    isGuidedDemoActive,
    isInAssignedToMe,
    navigateToDemoConversation,
    guidedDemoConversationId,
    isTeammateTourActive,
    completedStartGuideTasks,
    hasNonGlobalAdminStartGuideFlag,
    shouldShowReadStatusBubble,
    shouldShowPrivateCommentBubble,
    shouldTrackNavigationFromDemoInbox,
    shouldShowFirstArchiveInfoBubble,
    openStartGuide,
  } = props;
  const [infoBubbles, setInfoBubbles] = useState<InfoBubblesMap>({});

  // Whether we've scheduled the comment composer info bubble to open.
  const hasScheduledCommentComposerBubbleRef = useRef<boolean>(false);
  const [setTimeout] = useTimeout();

  const demoDraftIdRef = useRef<number>();

  // Ref to keep track of whether we were previously watching navigation away from the demo inbox.
  // If we were tracking, and our incoming shouldTrackNavigationFromDemoInbox prop is false, we know
  // we need to open the start guide.
  // This is required so that the user can view the demo inbox without being distracted, but see the
  // start guide when they finish.
  const isTrackingNavigationFromDemoInboxRef = useRef<boolean>(shouldTrackNavigationFromDemoInbox);

  /*
   * Context methods.
   */

  /** Open an info bubble. */
  const openInfoBubble = useCallback(
    (infoBubble: OnboardingTipTypesEnum) => {
      // If we're already trying to open that infoBubble no need to run another callback/rerender.
      if (infoBubbles[infoBubble]) {
        return;
      }

      globalWindow.requestInstrumentedIdleCallback(
        () => {
          setInfoBubbles((previous) => {
            if (previous[infoBubble]) {
              return previous;
            }

            return {...previous, [infoBubble]: true};
          });
        },
        {source: TaskSourcesEnum.USER_INTERACTION},
      );
    },
    [infoBubbles],
  );

  const useOpenInfoBubbleEffect = (infoBubble: OnboardingTipTypesEnum, shouldOpenInfoBubble: boolean) =>
    useEffect(() => {
      if (seenOnboardingTips.includes(infoBubble)) {
        return;
      }

      if (shouldOpenInfoBubble) {
        openInfoBubble(infoBubble);
      }
    }, [infoBubble, shouldOpenInfoBubble]);

  /** Close an info bubble. */
  const closeInfoBubbles = useCallback(
    (...bubblesToClose: ReadonlyArray<OnboardingTipTypesEnum>) => {
      // Mark the bubble as seen.
      updateSeenOnboardingTips(bubblesToClose);

      setInfoBubbles((previous) => {
        const newBubbles = {...previous};
        bubblesToClose.forEach((bubble) => {
          newBubbles[bubble] = false;
        });

        return newBubbles;
      });
    },
    [updateSeenOnboardingTips],
  );

  /*
   * State listeners.
   */

  // Find or return the id of the demo draft.
  const updateDemoDraftTracking = useCallback(
    (state: AppState) => {
      if (demoDraftIdRef.current) {
        return;
      }

      // The demo draft must belong to the demo conversation, have status DRAFT_COMPOSE, be a reply, and include the blurb sample.
      const demoDraft = Object.values(state.data.messages).find(
        (m) =>
          m &&
          m.conversationId === guidedDemoConversationId &&
          m.status === MessageStatusesEnum.DRAFT_COMPOSE &&
          m.isReply &&
          m.blurb.includes(demoDraftBlurbSample),
      );

      demoDraftIdRef.current = demoDraft && demoDraft.id;
    },
    [guidedDemoConversationId],
  );

  // Schedule the conversation composer bubble to open if the conversation viewer bubble is open.
  const maybeOpenGuidedDemoCommentComposerBubble = useCallback(() => {
    if (
      infoBubbles[OnboardingTipTypesEnum.GUIDED_DEMO_CONVERSATION_VIEWER] &&
      !infoBubbles[OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER] &&
      !hasScheduledCommentComposerBubbleRef.current
    ) {
      hasScheduledCommentComposerBubbleRef.current = true;

      // Schedule the comment composer bubble to open after a brief delay.
      setTimeout(() => {
        openInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER);
      }, 700);
    }
  }, [infoBubbles, openInfoBubble, setTimeout]);

  // Close the comment composer info bubble if the user mentions the demo teammate.
  const maybeCloseGuidedDemoCommentComposerBubble = useCallback(
    (state: AppState) => {
      if (!guidedDemoConversationId) {
        return;
      }

      const teammates = getTeammates(state);
      const comments = getComments(state);

      const userComment = Object.values(comments).find((c) => {
        if (!c || c.conversationId !== guidedDemoConversationId || c.status !== CommentStatusesEnum.SENT) {
          return false;
        }

        const mentionedTeammates = findMentionedTeammates(userId, teammates, c.text);
        return mentionedTeammates.some((t) => t.isGuidedDemo);
      });

      if (!userComment) {
        return;
      }

      closeInfoBubbles(OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER);
    },
    [closeInfoBubbles, guidedDemoConversationId, userId],
  );

  // Open the edit draft info bubble if we've seen the comment composer bubble and we can find the draft.
  const maybeOpenGuidedDemoEditDraftBubble = useCallback(
    (state: AppState) => {
      // If we haven't already seen the comment composer bubble, we shouldn't open the edit draft one.
      if (!seenOnboardingTips.includes(OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER)) {
        return;
      }

      if (!guidedDemoConversationId) {
        return;
      }

      // Find the demo draft.
      updateDemoDraftTracking(state);
      if (!demoDraftIdRef.current) {
        return;
      }

      openInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_EDIT_DRAFT);
    },
    [guidedDemoConversationId, openInfoBubble, seenOnboardingTips, updateDemoDraftTracking],
  );

  // Close the edit draft bubble if the draft is taken over by the user.
  const maybeCloseGuidedDemoEditDraftBubble = useCallback(
    (state: AppState) => {
      updateDemoDraftTracking(state);
      if (!demoDraftIdRef.current) {
        return;
      }

      const demoDraft = Object.values(state.data.messages).find((m) => m && m.id === demoDraftIdRef.current);
      if (!demoDraft || demoDraft.authorId !== userId) {
        return;
      }

      closeInfoBubbles(OnboardingTipTypesEnum.GUIDED_DEMO_EDIT_DRAFT);
    },
    [closeInfoBubbles, updateDemoDraftTracking, userId],
  );

  // Close the composer send bubble if the demo draft was sent.
  const maybeCloseGuidedDemoComposerSendBubble = useCallback(
    (state: AppState) => {
      if (!demoDraftIdRef.current) {
        return;
      }

      const sentDraft = Object.values(state.data.messages).find(
        (m) =>
          m &&
          m.id === demoDraftIdRef.current &&
          (m.status === MessageStatusesEnum.DRAFT_PENDING ||
            m.status === MessageStatusesEnum.DRAFT_SENDING ||
            m.status === MessageStatusesEnum.SENT),
      );
      if (!sentDraft) {
        return;
      }

      closeInfoBubbles(
        OnboardingTipTypesEnum.GUIDED_DEMO_COMPOSER_SEND,
        OnboardingTipTypesEnum.GUIDED_DEMO_CONVERSATION_VIEWER,
      );
    },
    [closeInfoBubbles],
  );

  // Close the comment composer info bubble if the user mentions a teammate.
  const maybeCloseStartGuideCommentComposerBubble = useCallback(
    (state: AppState) => {
      const mentionedTeammate = state.data.preferences.completedStartGuideTasks.includes(
        StartGuideTaskTypesEnum.MENTION_TEAMMATE,
      );

      if (!mentionedTeammate) {
        return;
      }

      closeInfoBubbles(OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER);
    },
    [closeInfoBubbles],
  );

  /** p2 read status */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.READ_STATUS_HIGHLIGHT, shouldShowReadStatusBubble);
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.READ_STATUS_CELL, shouldShowReadStatusBubble);

  /** private message comment  */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.PRIVATE_INBOX_COMMENT, shouldShowPrivateCommentBubble);

  /** shared message comment  */
  useOpenInfoBubbleEffect(OnboardingTipTypesEnum.SHARED_INBOX_COMMENT, shouldShowPrivateCommentBubble);

  // Open the archive info bubble if a user comments for the first time
  useEffect(() => {
    if (!shouldShowFirstArchiveInfoBubble || infoBubbles[OnboardingTipTypesEnum.FIRST_CONVERSATION_ARCHIVE]) {
      return;
    }

    openInfoBubble(OnboardingTipTypesEnum.FIRST_CONVERSATION_ARCHIVE);
  }, [openInfoBubble, shouldShowFirstArchiveInfoBubble, infoBubbles]);

  // Handles watching navigation from the demo inbox to open the start guide.
  useEffect(() => {
    // If we were not watching for navigation before, but should now, update the ref.
    if (!isTrackingNavigationFromDemoInboxRef.current && shouldTrackNavigationFromDemoInbox) {
      isTrackingNavigationFromDemoInboxRef.current = true;
    }

    // Otherwise, if we were tracking, and the latest prop says we should no longer be tracking,
    // we know we need to open the start guide.
    if (isTrackingNavigationFromDemoInboxRef.current && !shouldTrackNavigationFromDemoInbox) {
      isTrackingNavigationFromDemoInboxRef.current = false;
      openStartGuide();
    }
  }, [openStartGuide, shouldTrackNavigationFromDemoInbox]);

  useEffect(() => {
    // If neither the guided demo nor the teammate tour are active, check if we need to watch for changes in startguide comment composer task.
    if (!isGuidedDemoActive && !isTeammateTourActive) {
      if (
        hasNonGlobalAdminStartGuideFlag &&
        !completedStartGuideTasks.includes(StartGuideTaskTypesEnum.MENTION_TEAMMATE)
      ) {
        return store.subscribe(() => {
          const state = store.getState();
          const shouldWatchStateToCloseInfoBubble = (infoBubble: OnboardingTipTypesEnum) =>
            !isInfoBubbleUnseen(infoBubbles, state)(infoBubble);

          if (shouldWatchStateToCloseInfoBubble(OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER)) {
            maybeCloseStartGuideCommentComposerBubble(state);
          }
        });
      }

      return undefined;
    }

    // We need to be in assigned to me for the guided demo, unless we've already sent the message.
    if (
      isGuidedDemoActive &&
      !isInAssignedToMe &&
      !seenOnboardingTips.includes(OnboardingTipTypesEnum.GUIDED_DEMO_COMPOSER_SEND)
    ) {
      navigateToDemoConversation();
      return undefined;
    }

    // The active sequence includes only unseen bubbles in the sequence, so if there is one, we should open the first entry.
    // This allows bubbles to open "automatically" once the prior bubble in the sequence is closed.
    if (activeSequence && !infoBubbles[activeSequence[0]]) {
      openInfoBubble(activeSequence[0]);
    }

    // We do not need to watch the state if we are not in the guided demo.
    if (!isGuidedDemoActive) {
      return undefined;
    }

    return store.subscribe(() => {
      const state = store.getState();
      const shouldWatchStateToOpenInfoBubble = isInfoBubbleUnseen(infoBubbles, state);
      const shouldWatchStateToCloseInfoBubble = (infoBubble: OnboardingTipTypesEnum) =>
        !isInfoBubbleUnseen(infoBubbles, state)(infoBubble);

      if (shouldWatchStateToOpenInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER)) {
        maybeOpenGuidedDemoCommentComposerBubble();
      }

      if (shouldWatchStateToCloseInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_COMMENT_COMPOSER)) {
        maybeCloseGuidedDemoCommentComposerBubble(state);
      }

      if (shouldWatchStateToOpenInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_EDIT_DRAFT)) {
        maybeOpenGuidedDemoEditDraftBubble(state);
      }

      if (shouldWatchStateToCloseInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_EDIT_DRAFT)) {
        maybeCloseGuidedDemoEditDraftBubble(state);
      }

      if (shouldWatchStateToCloseInfoBubble(OnboardingTipTypesEnum.GUIDED_DEMO_COMPOSER_SEND)) {
        maybeCloseGuidedDemoComposerSendBubble(state);
      }
    });
  }, [
    store,
    infoBubbles,
    seenOnboardingTips,
    maybeCloseGuidedDemoCommentComposerBubble,
    maybeOpenGuidedDemoCommentComposerBubble,
    maybeOpenGuidedDemoEditDraftBubble,
    maybeCloseGuidedDemoEditDraftBubble,
    maybeCloseGuidedDemoComposerSendBubble,
    isGuidedDemoActive,
    isInAssignedToMe,
    navigateToDemoConversation,
    activeSequence,
    openInfoBubble,
    isTeammateTourActive,
    maybeCloseStartGuideCommentComposerBubble,
    completedStartGuideTasks,
    hasNonGlobalAdminStartGuideFlag,
  ]);

  const context = useMemo<InfoBubblesContextProps>(
    () => ({infoBubbles, openInfoBubble, closeInfoBubbles}),
    [closeInfoBubbles, infoBubbles, openInfoBubble],
  );

  return <InfoBubblesContext.Provider value={context}>{props.children}</InfoBubblesContext.Provider>;
};

/*
 * Helpers.
 */

function isInfoBubbleUnseen(infoBubbles: InfoBubblesMap, state: AppState) {
  // An info bubble is unseen if it has not been saved to `seenOnboardingTips`, and is not currently open.
  return (infoBubble: OnboardingTipTypesEnum): boolean =>
    !state.data.preferences.seenOnboardingTips.includes(infoBubble) && !infoBubbles[infoBubble];
}
